import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Input, ListRow, Tag } from '@rallyshift/design-system'
import api, { PlatformOrganizationSummary } from '../../api/client'
import { ROOT_DOMAIN } from '../../api/tenant'

const ACTION_LABELS: Record<string, string> = {
  'organization.create': 'Aangemaakt',
  'organization.delete': 'Verwijderd',
  'organization.subscription': 'Abonnement',
  'organization.domain': 'Eigen domein',
  'organization.export': 'Export',
  'organization.import': 'Import',
  'email.test': 'E-mailtest',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Actief',
  suspended: 'Opgeschort',
  cancelled: 'Opgezegd',
}

const PlatformOrgsPage: React.FC = () => {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<PlatformOrganizationSummary[]>([])
  const [audit, setAudit] = useState<
    { id: string; actor_email: string; action: string; organization_slug: string | null; created_at: string }[]
  >([])
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.platform.listOrganizations(), api.platform.audit()])
      .then(([list, entries]) => {
        if (cancelled) return
        setOrgs(list)
        setAudit(entries)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Organisaties konden niet worden geladen.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return orgs
    return orgs.filter(
      (o) =>
        o.name.toLowerCase().includes(term) ||
        o.slug.toLowerCase().includes(term) ||
        (o.contact_email || '').toLowerCase().includes(term),
    )
  }, [orgs, search])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-display-md text-text-primary">Organisaties</h1>
          <p className="mt-1 text-md text-text-secondary">
            Elke klant, met het abonnement dat jij eraan hangt.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => navigate('/platform/import')}>
            Importeren
          </Button>
          <Button onClick={() => navigate('/platform/new')}>Nieuwe organisatie</Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="mb-3">
          <Input
            label="Zoeken"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Naam, webadres of e-mail"
          />
        </div>
        {loading ? (
          <p className="text-md text-text-secondary">Laden…</p>
        ) : filtered.length === 0 ? (
          <p className="text-md text-text-secondary">
            {orgs.length === 0
              ? 'Er is nog geen organisatie. Maak er een, of importeer een export.'
              : 'Geen organisatie die aan die zoekterm voldoet.'}
          </p>
        ) : (
          <div className="-mx-5">
            {filtered.map((org) => (
              <ListRow
                key={org.id}
                title={org.name}
                meta={`${org.slug}.${ROOT_DOMAIN} · ${org.members} leden · ${org.events} evenementen · ${org.shifts} diensten`}
                trailing={
                  <span className="flex flex-wrap items-center gap-2">
                    <Tag>{org.plan}</Tag>
                    <Tag>{STATUS_LABELS[org.status] || org.status}</Tag>
                  </span>
                }
                onClick={() => navigate(`/platform/organizations/${org.id}`)}
              />
            ))}
          </div>
        )}
      </Card>

      {audit.length > 0 && (
        <Card title="Recente acties" meta="Wat er vanuit dit beheer is gedaan.">
          <div className="-mx-5">
            {audit.slice(0, 12).map((entry) => (
              <ListRow
                key={entry.id}
                title={ACTION_LABELS[entry.action] || entry.action}
                meta={`${entry.actor_email}${entry.organization_slug ? ` · ${entry.organization_slug}` : ''} · ${new Date(entry.created_at).toLocaleString('nl-NL')}`}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

export default PlatformOrgsPage
